<template>
  <div class="container-xl position-relative" style="max-width: 700px">
    <notice v-if="lineLoginNotice" class="mb-3">{{ lineLoginNotice }}</notice>

    <notice v-if="lineLoginError" variant="danger" class="mb-3">{{ lineLoginError }}</notice>
    <line-login-button
      v-if="urlInfo && urlInfo.lineLoginFormDisplay && !lineLoginNotice"
      origin="lp_form"
      :url-id="urlInfo.id"
    />

    <div class="border p-3 mb-3" v-if="displayAmazonPayButton">
      <div class="d-flex justify-content-center">
        <span class="amazon-title mb-3">Amazonアカウントで簡単お申込み!</span>
      </div>
      <div class="d-flex justify-content-center mb-3">
        <AmazonPayButton :payloads="amazonPayPayload" />
      </div>

      <div class="amazon-text mb-3">
        <span>
          Amazonに登録しているお支払い情報、お届け先情報を利用して簡単にお申し込みが可能です
        </span>
      </div>
    </div>

    <section v-show="!isLoggedIn">
      <div class="border p-3">
        <b-checkbox
          :checked="showSignInForm"
          :value="true"
          :unchecked-value="false"
          @input="showSignInForm = $event"
          name="login_check"
          id="login_check"
        >
          入力を省略する（当サイトで購入したことがある方）
        </b-checkbox>
      </div>
      <customer-sign-in v-show="showSignInForm" type="cart" :amazonInfo="amazonInfo" />
    </section>
    <div v-show="!showSignInForm && urlInfo">
      <div class="py-3">
        下記お客さま情報を全てご記入いただき、お申し込みボタンをクリックしてください。
      </div>
      <div class="p-3 base-errors" v-if="checkBaseMessage">
        {{ baseMessage }}
      </div>

      <div class="p-3 base-errors" v-if="errorsHeader.length">
        <div v-for="(error, index) in errorsHeader" :key="index">
          {{ error }}
        </div>
      </div>

      <CartProduct
        v-if="formDataReady"
        :products="urlInfo.products"
        :buyMultipleVariant="urlInfo.buyMultipleVariant"
        :productQuantityDisplay="urlInfo.productQuantityDisplay"
        :generalVariantDisplay="urlInfo.generalVariantDisplay"
        :generalAndOwnVariantDisplay="generalAndOwnVariantDisplay"
        :ownVariantDisplay="urlInfo.ownVariantDisplay"
        :productsUrls="urlInfo.productsUrls"
      />

      <CartBillingAddress />

      <CartCustomer
        v-if="formDataReady"
        :confirmEmailDisplay="urlInfo.confirmEmailDisplay"
        :passwordDisplay="urlInfo.passwordDisplay"
        :confirmPasswordDisplay="urlInfo.confirmPasswordDisplay"
        :sexRequiredEnabled="urlInfo.sexRequiredEnabled"
        :birthdayRequiredEnabled="urlInfo.birthdayRequiredEnabled"
        :sexDisplay="urlInfo.sexDisplay"
        :birthdayDisplay="urlInfo.birthdayDisplay"
      />

      <CartShippingAddress
        v-if="formDataReady"
        :setValues="setFormValues"
        :disabledPaymentAndShipping="disabledPaymentAndShipping"
      />

      <CartPayment
        v-if="formDataReady"
        :options="urlInfo.availablePaymentMethodOptions"
        :payment-methods="urlInfo.availablePaymentMethods"
        :disabledPaymentAndShipping="disabledPaymentAndShipping"
      />

      <CartShippingCarrier
        v-if="formDataReady"
        :buyMultipleVariant="urlInfo.buyMultipleVariant"
        :deliveryCompanyDisplay="urlInfo.deliveryCompanyDisplay"
      />

      <specify-delivery-date :no-padding="false" :order-mode="NORMAL_MODE" />

      <CartCoupon
        v-if="formDataReady && urlInfo.couponDisplay"
        :availableCouponListDisplay="urlInfo.availableCouponListDisplay"
        :availableCoupons="cartStore.availableCoupons"
      />

      <SkipConfirmSummary v-if="formDataReady && urlInfo.skipConfirm" />

      <div class="mt-4 pt-2 mb-3" v-if="urlInfo.optinDisplay">
        <b-checkbox v-model="optin" name="mailoptin" id="mailoptin">
          ショップからのお知らせを受け取る
        </b-checkbox>
      </div>

      <form-validator class="mt-4 pt-2 mb-3" float name="termAccepted" clientName="termAccepted">
        <b-checkbox v-model="termAccepted" name="agree_check" id="agree_check">
          <a href="/info/customer_term" target="_blank">利用規約</a>
          に同意して申込みます。未成年者については法定代理人の同意を得ていることを確認します
        </b-checkbox>
      </form-validator>

      <div class="text-center my-5">
        <div
          class="d-flex justify-content-center mb-3"
          v-if="
            paymentMethodCode == 'amazon_pay' &&
            cartStore.checkoutSessionSignature &&
            cartStore.orderSummary &&
            urlInfo.skipConfirm
          "
        >
          <AmazonPayButton
            :amazonId="`AmazonPayButtonLP`"
            :formValues="formValues"
            :payloads="amazonPayloads"
          />
        </div>
        <div
          class="text-danger"
          v-else-if="
            paymentMethodCode == 'amazon_pay' && !cartStore.orderSummary && urlInfo.skipConfirm
          "
        >
          <span>ご面倒をおかけしますが、情報を全てご記入ください。 </span>
        </div>

        <b-button
          v-else
          size="lg"
          variant="success"
          type="submit"
          @click="submitForm"
          name="submit"
          id="submit"
        >
          {{ urlInfo.skipConfirm ? "ご注文完了へ" : "確認ページへ進む" }}
        </b-button>
      </div>

      <validation-status></validation-status>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref, computed, onBeforeMount, watch } from "vue";
import { useField } from "vee-validate";
import useCartValidation from "@/composables/useCartValidation";

import CartProduct from "@/components/organisms/pages/cart/CartProduct.vue";
import CartBillingAddress from "@/components/organisms/pages/cart/CartBillingAddress.vue";
import CartShippingCarrier from "@/components/organisms/pages/cart/CartShippingCarrier.vue";
import CartCustomer from "@/components/organisms/pages/cart/CartCustomer.vue";
import CartShippingAddress from "@/components/organisms/pages/cart/CartShippingAddress.vue";
import CartPayment from "@/components/organisms/pages/cart/CartPayment.vue";
import CustomerSignIn from "@/components/organisms/pages/customer/CustomerSignIn.vue";
import SpecifyDeliveryDate from "@/components/organisms/pages/cart/SpecifyDeliveryDate.vue";
import SkipConfirmSummary from "@/components/organisms/pages/cart/SkipConfirmSummary.vue";
import CartCoupon from "@/components/organisms/pages/cart/CartCoupon.vue";
import AmazonPayButton from "@/components/molecules/pages/cart/payment/amazon_pay/AmazonPayButton.vue";
import ValidationStatus from "@/components/molecules/common/form/ValidationStatus.vue";
import FormValidator from "@/components/molecules/common/form/FormValidator.vue";
import LineLoginButton from "@/components/molecules/line/LineLoginButton.vue";

import { useCartStore } from "@/stores/cart";
import { useOrderStore } from "@/stores/order";
import { useAuthStore } from "@/stores/auth";
import { useGlobalStore } from "@/stores/global";
import { useBaseInfoStore } from "@/stores/base_info";
import { useValidationStore } from "@/stores/form/validation";

import useGraphql from "@/composables/useGraphql";
import * as lineLinkQueries from "@/graphql/queries/publics/line_link";

import { cloneDeep } from "lodash";

import { ProductType } from "@/types/ProductType";

import { NORMAL_MODE, CARD_REQUIRED_PAYMENT_METHOD_CODES } from "@/helpers/constants";
import { PAYGENT_PAYMENT_METHOD_CODE } from "@/helpers/constants";
import { omit } from "lodash";
import { AddressInput } from "@/types/input/CartInput";
import { findByCode } from "jp-prefectures";

interface Props {
  baseUrl: string;
  payjpToken: string;
  confirmPath: string;
  upsellPath: string;
  gmoShopId: string;
  merchantId: string;
  serviceId: string;
  paygentInfo: string;
  amazonInfo: any;
  amazonSignature: string;
}

const props = defineProps<Props>();

const globalStore = useGlobalStore();
const orderStore = useOrderStore();
const baseInfoStore = useBaseInfoStore();
const validationStore = useValidationStore();

const baseErrors: any = computed(() => globalStore.validationErrors);

const baseMessage = computed(() => {
  if (baseErrors.value && baseErrors.value.base) {
    return baseErrors.value.base[Object.keys(baseErrors.value.base)[0]][0];
  } else return;
});

const checkBaseMessage = computed(() => {
  if (globalStore.keepError && baseErrors.value.base) {
    return true;
  } else {
    return false;
  }
});

const errorsHeader = computed(() => globalStore.validationBaseErrors ?? []);

// Task 8's return protocol (the link payload itself stays in Redis; all the
// browser ever holds is the opaque key in stores/line.ts):
//   line_login=1  -> login token issued, shop-token cookie set (case 1)
//   line_linked=1 -> already linked, or linked just now; no login change
//   line_auth=1   -> case-2 authentication completed, link key handed over
//   line_error=.. -> failure message (Japanese)
// The LINE email is NOT among these: it is fetched from the server against the
// link key, so it never appears in the URL, the Referer or analytics. The key
// is not among them either, and for the same reason — it arrives as a POST
// field on the handoff form, not as a parameter here.
const lineReturnParams = new URLSearchParams(window.location.search);

// Past this, a stalled prefill degrades to "no prefill" rather than delaying
// the purchase form any further.
const LINE_PREFILL_TIMEOUT_MS = 3000;

// D4: these two notices are shown on the LP purchase form ONLY.
const lineLoginNotice = computed(() => {
  // line_login=1 is only ever issued for an already-linked LINE ID (case 1),
  // so both params map to the same issue-§3.4.1 "already linked" notice.
  if (lineReturnParams.get("line_linked") === "1" || lineReturnParams.get("line_login") === "1")
    return "既にLINE連携済みのためログインいたしました。";
  if (lineReturnParams.get("line_auth") === "1")
    return "認証が完了しました。このまま購入が完了するとLINE連携が完了します。";
  return null;
});

const lineLoginError = computed(() => lineReturnParams.get("line_error"));

const amazonInfo = computed(() => JSON.parse(props.amazonInfo));

const cartStore = useCartStore();

// Fix Prevent safari loading from cache when back button is clicked
window.onpageshow = function (event) {
  if (event.persisted) {
    window.location.reload();
  }
};

const urlData = cartStore.cart.urlData;

const authStore = useAuthStore();

const isLoggedIn = computed(() => authStore.isLoggedIn);

const urlInfo = computed(() => cartStore.urlInfo);

// When sign in amazon pay
if (amazonInfo.value?.token) authStore.setTokenAndCookie(amazonInfo.value.token);

var {
  setValues: setFormValues,
  setFieldValue,
  resetForm,
  values: formValues,
  setErrors,
  validate,
} = useCartValidation(isLoggedIn.value, cartStore.cart, urlInfo.value);

const selectedProductId = computed(() => {
  return productsData.value[0].id;
});

const generalAndOwnVariantDisplay = computed(() => {
  return urlInfo.value.generalVariantDisplay && urlInfo.value.ownVariantDisplay;
});

const quantityProductUrlDisplay = computed(() => {
  return generalAndOwnVariantDisplay.value && urlInfo.value.productQuantityDisplay;
});

// Check Product Upsell After Add Array Selected
const productUpsell = computed(() => {
  return (
    addProductUpsell.value && selectedProductId.value == (urlInfo.value.products.at(-1)?.id as any)
  );
});

const displayAmazonPayButton = computed(
  () =>
    urlInfo.value.availablePaymentMethodOptions.some((item) => item.value === "amazon_pay") &&
    !isLoggedIn.value &&
    formDataReady.value,
);

const isCardRequired = computed(() =>
  CARD_REQUIRED_PAYMENT_METHOD_CODES.includes(paymentMethodCode.value),
);

const formDataReady = ref(false);

const addProductUpsell = ref(false);

const showSignInForm = ref(false);

const disabledPaymentAndShipping = ref(false);

const fetchUrl = async () => {
  await cartStore.fetchUrl(props.baseUrl);

  const defaultProductUrl = urlInfo.value.productsUrls.find((p) => p.default == true);

  const defaultProduct = urlInfo.value?.products.find(
    (item) => item.id === defaultProductUrl?.productId,
  );

  const firstProduct = urlInfo.value.products[0];

  if (!firstProduct) return;

  if (urlInfo.value.buyMultipleVariant) {
    if (defaultProduct) {
      const index = urlInfo.value.products.indexOf(defaultProduct);
      urlInfo.value.products.splice(index, 1);

      urlInfo.value.products.unshift(defaultProduct);
    }

    cartStore.cart.productsData = urlInfo.value.products.map((item) => {
      return defaultProductData(item);
    });
  } else {
    cartStore.cart.productsData = [
      defaultProductData(defaultProduct ? defaultProduct : firstProduct),
    ];

    if (urlInfo.value.upsell && firstProduct) {
      if (urlData.upsellBack && urlData.productUpsellId) {
        // Find Product Upsell And Add To Array
        const product = urlInfo.value?.products.find((item) => item.id === urlData.productUpsellId);

        if (product) {
          if (!urlInfo.value?.products.map((item) => item.id).includes(product?.upsellProduct.id)) {
            urlInfo.value.products.push(product.upsellProduct);

            addProductUpsell.value = true;
          }
        }
      }
    }
  }
  if (urlInfo.value.availablePaymentMethodOptions.length && !paymentMethodCode.value) {
    cartStore.cart.paymentData.paymentMethodCode =
      urlInfo.value.availablePaymentMethodOptions[0].value;
  }
};

const fetchCustomer = async () => {
  if (!isLoggedIn.value && !amazonInfo.value?.token) return;

  await cartStore.fetchCustomer();
};

const upsellProductSelected = computed(() => {
  const product = urlInfo.value?.products.find((item) => item.id === selectedProductId.value);

  if (productUpsell.value) {
    return false;
  } else {
    return urlInfo.value?.upsell && !!product?.upsellProduct;
  }
});

const defaultProductData = (product: ProductType) => {
  const productUrl = urlInfo.value.productsUrls.find(
    (productUrl) => productUrl.productId === product.id,
  );

  const quantity = quantityProductUrlDisplay.value ? productUrl?.minQuantity : product.purchaseMin;

  const arrayLength = generalAndOwnVariantDisplay.value ? quantity : 1;

  const variantId = product.variantCombinations ? null : product.variantMasterId;

  const newOptionsValue = [];
  for (let index = 0; index < (arrayLength || 0); index++) {
    newOptionsValue.unshift({
      variantId: variantId,
      quantity: null,
      valueOptions: [
        {
          id: null,
          value: null,
        },
      ],
    });
  }

  return {
    id: product.id,
    quantity: quantity ? quantity : product.purchaseMin,
    variantData: newOptionsValue,
    isRecurring: product.isRecurring,
  };
};

const validateCart = async () => {
  orderStore.offerPosition = null;
  const data = await cartStore.validateCart();
  const query = `?u=${cartStore.urlInfo.baseUrl}`;

  if (!data) return;
  else if (upsellProductSelected.value) {
    window.location.href = props.upsellPath + query + "&product_id=" + selectedProductId.value;
  } else {
    window.location.href = props.confirmPath + query;
  }
};

const paygentEnableThreeDSecure = computed(() => {
  if (props.paygentInfo) {
    const paygentInfo = JSON.parse(props.paygentInfo);
    return paygentInfo.enable_three_d_secure;
  } else {
    return false;
  }
});

const submitCart = () => {
  if (
    cartStore.cart.paymentData.paymentMethodCode == PAYGENT_PAYMENT_METHOD_CODE &&
    paygentEnableThreeDSecure.value
  ) {
    const paymentData = omit(cartStore.cart.paymentData, ["isAddNewCard", "card"]);
    const input = {
      paymentData: paymentData,
      customerData: cartStore.cart.customerData,
      amount: String(cartStore.orderSummary?.total),
    };
    cartStore
      .orderPaymentPaygentThreeDSecure(input)
      .then((data: any) => {
        if (data.token) authStore.setTokenAndCookie(data.token);
        if (data.customerCardId) {
          cartStore.cart.paymentData.sourceId = data.customerCardId;
        }
        window.location.href = data.redirectUrl;
      })
      .catch((error) => {
        cartStore.handleErrorWhenSubmit(error);
      });

    return;
  }

  cartStore
    .submitCart()
    .then((data: any) => {
      if (data.token) authStore.setTokenAndCookie(data.token);
      if (data.cvUpsellAvailable || data.cvXsellAvailable) {
        window.location.href =
          "/lp/cv_upsell" +
          window.location.search +
          "&order_id=" +
          data.orderNumber +
          "&offer_position=0";

        orderStore.offerPosition = null;
      } else {
        cartStore.$reset();
        window.location.href =
          "/lp/finish" + window.location.search + "&order_id=" + data.orderNumber;
      }
      orderStore.resetPageView(props.baseUrl);
    })
    .catch((error) => {
      console.log(error);
    });
};

const resetErrors = () => {
  if (!globalStore.keepBaseError) globalStore.resetValidationBaseErrors();

  globalStore.keepBaseError = false;
};

const handleAmazonPayReturn = () => {
  const newUrl = window.location.href.split("&buyerToken")[0];
  history.pushState({}, document.title, newUrl);

  if (amazonInfo.value?.token || authStore.customerExisted) {
    authStore.customerExisted = true;
    paymentMethodCode.value = "amazon_pay";
    disabledPaymentAndShipping.value = true;
  }

  if (amazonInfo.value) showSignInForm.value = amazonInfo.value.result;
};

// Issue §3.3: prefill the email obtained from LINE (case 2, email permission
// granted). The address is read from the server, scoped to the link key held
// in stores/line.ts — never from the URL, which would expose it to every third-party
// script on the LP through the Referer header before any of this code runs.
// When email_permission is off or LINE returned no email the field is null and
// this is a no-op, leaving the input empty (§1.1: 動作はするが、メールアドレス
// を自動取得できない). Prefill only: the visitor can still edit it. Case 1 never
// reaches this branch — the server logs the customer in and the existing
// logged-in flow shows the system-registered email (§3.3, tests 25/26).
const lineLinkEmail = ref<string | null>(null);

// Fetched inside the onMounted batch rather than after it, and bounded: this
// is an optional prefill on the purchase form, so it must never sit on the
// critical path. useFetch has no timeout, so an awaited call that hangs would
// leave formDataReady false and the form would never render at all.
const fetchLineLinkEmail = async () => {
  if (lineReturnParams.get("line_auth") !== "1") return;

  // errorToasted: the toast is raised inside useGraphql, so a .catch here
  // could not suppress it, and a missed prefill must never put a red toast
  // over the purchase form. loading: a line_auth visitor is by definition a
  // guest, and useGraphql registers no request without a token anyway.
  const request = useGraphql<{ email: string | null }>(
    lineLinkQueries.lineLink,
    {},
    { errorToasted: false, loading: false },
  ).catch(() => null);

  const link = await Promise.race([
    request,
    new Promise<null>((resolve) => setTimeout(() => resolve(null), LINE_PREFILL_TIMEOUT_MS)),
  ]);

  lineLinkEmail.value = link?.email ?? null;
};

// Applied after resetForm so it is not clobbered by it. Prefill only: the
// visitor can still edit the field.
const applyLineLinkEmail = () => {
  if (!lineLinkEmail.value) return;

  setFieldValue("customerData.email", lineLinkEmail.value);
};

// The LINE round-trip params must not survive: submitCart rebuilds the next
// URL from window.location.search, so they would follow the visitor into
// /lp/finish and browser history. Stripping them also stops a reload from
// re-showing the notice and permanently suppressing the button. The notices
// still render - lineReturnParams is a snapshot taken at setup.
//
// line_email is kept in this list although the server no longer sends it: a
// visitor who started the flow just before this deploy still comes back with
// one, and it should be cleared rather than left in their history.
const stripLineReturnParams = () => {
  const url = new URL(window.location.href);
  const keys = ["line_login", "line_linked", "line_auth", "line_email", "line_error"];
  // Guarded: a URL round-trip re-encodes existing params (utm_content=50%OFF
  // becomes 50%25OFF, ?flag becomes flag=), and those are carried into
  // /lp/confirm and /lp/finish - so non-LINE traffic must not be touched.
  if (!keys.some((key) => url.searchParams.has(key))) return;

  keys.forEach((key) => url.searchParams.delete(key));
  history.replaceState({}, document.title, url.toString());
};

onBeforeMount(() => resetErrors());

onMounted(async () => {
  if (cartStore.resetRequired(props.baseUrl)) {
    // handle for reload to keep customerExisted
    if (cartStore.lastPage !== "sign_in") authStore.customerExisted = false;

    cartStore.$reset();
    globalStore.validationErrors = {};
  }

  cartStore.setCartProps(props);

  // Best-effort analytics, deliberately outside the batch below: the purchase
  // form's render must not wait on an untimed write. The store absorbs its own
  // rejection.
  orderStore.createCustomersAccessHistory(props.baseUrl);

  await Promise.allSettled([
    baseInfoStore.fetchBaseInfo(),
    fetchCustomer(),
    fetchUrl(),
    cartStore.fetchSelectOptions(),
    orderStore.createPageViewData(props.baseUrl),
    fetchLineLinkEmail(),
  ]);

  cartStore.orderInfo = null;
  cartStore.checkoutSessionSignature = null;

  if (cartStore.resetRequired(props.baseUrl)) resetForm({ values: cartStore.cart });

  handleAmazonPayReturn();

  applyLineLinkEmail();
  stripLineReturnParams();

  formDataReady.value = true;

  validationStore.setErrors = setErrors;

  window.addEventListener("beforeunload", () => (globalStore.keepError = false));
  window.addEventListener("unload", () => (globalStore.keepError = false));

  if (!isLoggedIn.value) {
    const defaultOptin = !!urlInfo.value?.defaultOptinEnabled;
    cartStore.cart.customerData.optin = defaultOptin;
    optin.value = defaultOptin;
  }

  cartStore.currentBaseUrl = props.baseUrl;
  cartStore.lastPage = "lp";
});

const amazonPayPayload = {
  merchantId: amazonInfo.value?.merchant_id,
  productType: "SignIn",
  placement: "Cart",
  signInConfig: {
    payloadJSON: {
      signInReturnUrl: amazonInfo.value?.sign_in_return_url,
      storeId: amazonInfo.value?.store_id,
      signInScopes: [
        "name",
        "email",
        "postalCode",
        "shippingAddress",
        "phoneNumber",
        "billingAddress",
      ],
    },
    signature: props.amazonSignature,
    publicKeyId: amazonInfo.value?.public_key_id,
  },
};

const assignDataAndSubmit = () => {
  cartStore.cart = cloneDeep(formValues);
  urlInfo.value.skipConfirm ? submitCart() : validateCart();
};

const submitForm = async () => {
  globalStore.keepError = false;
  // Reset upsell flag to avoid validating wrong product
  if (upsellProductSelected.value) {
    upsellBack.value = false;
  }
  upsellBack.value = productUpsell.value;

  cartStore.currentMode = NORMAL_MODE;

  validate().then(async (result) => {
    validationStore.submitAttempts++;

    const allKeysNotInValidationStore = Object.keys(result.errors).every(
      (key) => !(key in validationStore.fieldsValidStatus),
    );

    if (!allKeysNotInValidationStore && !result.valid) {
      document.querySelector(".invalid-feedback")?.scrollIntoView({ behavior: "smooth" });
      return;
    }

    if (!isAddNewCard.value && isCardRequired.value) {
      cardToken.value = "";
      cardTokenKey.value = "";
      assignDataAndSubmit();
      return;
    }

    switch (paymentMethodCode.value) {
      case "cod":
      case "gmo_postpay_seperate_shipment":
      case "np_postpay_wiz":
      case "gmo_postpay_package":
        cardToken.value = "";
        sourceId.value = "";
        assignDataAndSubmit();
        break;
      case "payjp":
      case "gmo_creditcard_one_time":
      case "paygent_creditcard_one_time":
        cartStore.submitWithNewCard((token: string) => {
          cardToken.value = token;
          assignDataAndSubmit();
        });
        break;
      case "sbps_creditcard_one_time":
        cartStore.submitWithNewCard((token: string, tokenKey: string) => {
          cardToken.value = token;
          cardTokenKey.value = tokenKey;
          assignDataAndSubmit();
        });
        break;
      default:
        cardToken.value = "";
        cardTokenKey.value = "";
        sourceId.value = "";
        assignDataAndSubmit();
        break;
    }
  });
};
const { value: paymentMethodCode }: any = useField("paymentData.paymentMethodCode");
const { value: cardToken } = useField("paymentData.cardToken");
const { value: cardTokenKey } = useField("paymentData.cardTokenKey");
const { value: sourceId } = useField("paymentData.sourceId");
const { value: productsData } = useField("productsData");
const { value: upsellBack } = useField("urlData.upsellBack");

// クーポン一覧（ご利用可能なクーポン）は専用 API で取得する。
// クーポン機能/一覧表示フラグが ON のときのみ呼び、選択商品（productsData = フォーム値）
// が変わるたびに再取得して、選択中商品の単価で表示判定を更新する。
function loadAvailableCoupons() {
  if (!urlInfo.value?.couponDisplay || !urlInfo.value?.availableCouponListDisplay) return;

  cartStore.fetchAvailableCoupons(props.baseUrl, productsData.value);
}

// 商品・SKU・数量の変更（vee-validate の productsData）を検知して再取得。
watch(productsData, loadAvailableCoupons, { deep: true });
// 初回（urlInfo/フォーム準備完了）でも取得する。
watch(
  () => formDataReady.value,
  (ready) => {
    if (ready) loadAvailableCoupons();
  },
);
const { value: isAddNewCard } = useField("paymentData.isAddNewCard");
const { value: termAccepted } = useField("termAccepted");
const { value: billingAddressData } = useField<AddressInput>("billingAddressData");
const { value: optin } = useField("customerData.optin");

const { value: paymentSchedule }: any = useField("subsOrderData.paymentSchedule");
const { value: scheduledToBeDeliveredEveryXMonth }: any = useField(
  "subsOrderData.scheduledToBeDeliveredEveryXMonth",
);
const { value: scheduledToBeDeliveredOnXthDay }: any = useField(
  "subsOrderData.scheduledToBeDeliveredOnXthDay",
);

const { value: scheduledToBeDeliveredEveryXDay }: any = useField(
  "subsOrderData.scheduledToBeDeliveredEveryXDay",
);

const { value: scheduledToBeDeliveredEveryXDayOfWeek }: any = useField(
  "subsOrderData.scheduledToBeDeliveredEveryXDayOfWeek",
);
const { value: scheduledToBeDeliveredOnXthDayOfWeek }: any = useField(
  "subsOrderData.scheduledToBeDeliveredOnXthDayOfWeek",
);

const addressDetails = computed(() => {
  return {
    stateOrRegion: encodeURIComponent(findByCode(billingAddressData.value.prefectureId)?.name),
    addressLine1: encodeURIComponent(billingAddressData.value.addr01),
    addressLine2: encodeURIComponent(billingAddressData.value.addr02),
    addressLine3: null,
    name: encodeURIComponent(
      `${billingAddressData.value.name01}${billingAddressData.value.name02}`,
    ),
    postalCode: `${billingAddressData.value.zip01}${billingAddressData.value.zip02}`,
    phoneNumber: `${billingAddressData.value.tel01}${billingAddressData.value.tel02}${cartStore.cart.billingAddressData.tel03}`,
  };
});

const amazonPayloads = computed(() => {
  const recurringData = {
    chargePermissionType: "Recurring",
    recurringMetadata: {
      frequency: {
        unit: "Variable",
        value: "0",
      },
      amount: {
        amount: String(cartStore.orderSummary?.total),
        currencyCode: "JPY",
      },
    },
  };

  const payloadJSON = {
    storeId: amazonInfo.value?.store_id,

    webCheckoutDetails: {
      checkoutReviewReturnUrl: null,
      checkoutResultReturnUrl: amazonInfo.value?.checkout_result_return_url,
      checkoutMode: "ProcessOrder",
    },
    paymentDetails: {
      paymentIntent: "Confirm",
      chargeAmount: {
        amount: String(cartStore.orderSummary?.total),
        currencyCode: "JPY",
      },
      allowOvercharge: !cartStore.orderSummary?.isRecurring,
    },

    addressDetails: {
      ...addressDetails.value,
      countryCode: "JP",
    },
  };

  if (cartStore.orderSummary?.isRecurring) Object.assign(payloadJSON, recurringData);

  return {
    merchantId: amazonInfo.value?.merchant_id,

    productType: "PayAndShip",
    placement: "Checkout",

    createCheckoutSessionConfig: {
      payloadJSON: payloadJSON,
      signature: cartStore.checkoutSessionSignature,
      publicKeyId: amazonInfo.value?.public_key_id,
    },
  };
});

watch(
  () => cartStore.orderSummary,
  () => {
    if (paymentMethodCode.value == "amazon_pay" && termAccepted.value) {
      cartStore.cart.subsOrderData.paymentSchedule = paymentSchedule.value;

      cartStore.cart.subsOrderData.scheduledToBeDeliveredEveryXMonth =
        scheduledToBeDeliveredEveryXMonth.value;
      cartStore.cart.subsOrderData.scheduledToBeDeliveredOnXthDay =
        scheduledToBeDeliveredOnXthDay.value;
      cartStore.cart.subsOrderData.scheduledToBeDeliveredEveryXDay =
        scheduledToBeDeliveredEveryXDay.value;
      cartStore.cart.subsOrderData.scheduledToBeDeliveredEveryXDayOfWeek =
        scheduledToBeDeliveredEveryXDayOfWeek.value;
      cartStore.cart.subsOrderData.scheduledToBeDeliveredOnXthDayOfWeek =
        scheduledToBeDeliveredOnXthDayOfWeek.value;

      cartStore.orderPaymentAmazonPayGetCreateCheckoutSessionSignature(
        addressDetails.value,
        String(cartStore.orderSummary?.total),
        cartStore.orderSummary?.isRecurring || false,
      );
    }
  },
  { deep: true },
);
</script>

<style lang="scss">
.card {
  .card-header {
    align-items: center;
    min-height: 50px;
    font-weight: 600;
  }
}
.amazon-title {
  font-size: 16px;
  font-weight: bold;
  color: #a4030b;
}
.amazon-text {
  display: flex;
  font-size: 11px;
  font-weight: bolder;
  justify-content: center;
}
</style>
